import * as Sentry from '@sentry/react';
import {
  createVehicle,
  deleteVehicle,
  getVehiclesOptions,
  updateVehicle,
} from 'api/v1/vehicles';
import { getClientsOptions, getSuggestedClients } from 'api/v2/clients';
import { getFormCustomData, getLockStatus } from 'api/v2/revenues';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { Badge } from 'components/atoms/Badge';
import { Button } from 'components/atoms/Button';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { Tooltip } from 'components/atoms/Tooltip';
import { Checkbox } from 'components/atoms/Form/Checkbox';
import {
  FakeField,
  FieldConnector,
  FieldErrorManager,
} from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import { getOptionsDict } from 'components/atoms/Form/Select/helpers';
import { Toggle } from 'components/atoms/Form/Toggle';
import { Menu } from 'components/atoms/Menu';
import PriceListButton from 'components/atoms/PriceListButton';
import UseRef from 'components/atoms/UseRef';
import UseState from 'components/atoms/UseState';
import Amount from 'components/molecules/CommonCells/Amount.component';
import { textToDateRange } from 'components/molecules/Fields/DateRangerPicker/helpers';
import NumberField from 'components/molecules/Fields/Number/Number';
import PaymentAmount from 'components/molecules/Fields/PaymentAmount/PaymentAmount.component';
import PaymentAmountHelper from 'components/molecules/Fields/PaymentAmount/PaymentAmountHelper.component';
import { PaymentLinkSelect } from 'components/molecules/Fields/PaymentLink';
import {
  revenueAlternativeTransactionsOptions,
  TransactionOption2,
} from 'components/molecules/Fields/PaymentLink/utils';
import PeriodHelper from 'components/molecules/Fields/Period/PeriodHelper.components';
import {
  createPeriodsOptions,
  periodToValue,
} from 'components/molecules/Fields/Period/utils';
import { Helper } from 'components/molecules/Helper';
import LinkedItemsProgress from 'components/molecules/LinkedItemsProgress';
import { InvoiceSubmitLockedByTaxes } from 'components/molecules/SubmitLockByTaxes.compopnent';
import CarsSelect from 'components/organisms/CarsSelect';
import TaxImpact from 'components/organisms/TaxImpact';
import ClientForm from 'components/pages/Clients/Form';
import type { ItemInput } from 'components/pages/PriceList/Form/FormSlideOver';
import PriceListFormSlideOver from 'components/pages/PriceList/Form/FormSlideOver';
import { ItemSearchField } from 'components/pages/Revenues/GeneratedRevenueForm/SharedControls/ItemsForm/ItemSearchField';
import StickyControlsContainer from 'components/StickyControlsContainer';
import { useRevenueAiMetadata } from 'contexts/AIMetadataProvider/useRevenueAiMetadata';
import dayjs from 'dayjs';
import useHasTransactions from 'hooks/Bank/useHasTransactions';
import useGetRevenuesVATRates from 'hooks/Revenues/useGetRevenuesVATRates';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import useSuperQuery from 'hooks/useSuperQuery';
import useFeatures from 'hooks/useFeatures';
import type { FormSlideOverManagerApi } from 'hooks/useFormSlideOverManager';
import useHighlighTaxCheckField from 'hooks/useHighlightTaxCheckField';
import SuggestedTransaction from 'components/molecules/Fields/PaymentLink/SuggestedTransaction.component';
import useInvalidateResources from 'hooks/useInvalidateResources';
import useModal from 'hooks/useModal';
import { groupBy, isEmpty, last, omit } from 'lodash';
import type { FC, MouseEventHandler, ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { ReactMarkdownOptions } from 'react-markdown/lib/react-markdown';
import { useQueryClient } from 'react-query';
import { AlertIdsEnum } from 'types/AlertIdsEnum';
import {
  RevenuesCacheKeys,
  SelectOptionsCacheKeys,
} from 'types/cacheKeys.types';
import type {
  LockStatusTypeCatgoriesed,
  VATReturnFrequency,
} from 'types/global.types';
import { CurrenciesEnum, Section } from 'types/global.types';
import type {
  InvoiceFormValues,
  InvoiceItem,
  PriceItem,
} from 'types/invoice.types';
import {
  InvoiceStatusEnum,
  InvoiceTypeEnum,
  ReviewStatusEnum,
  type Invoice,
} from 'types/invoice.types';
import type { IRevenue } from 'types/revenues.types';
import { RevenueStatus } from 'types/revenues.types';
import { TaxImpactDocumentType } from 'types/taxes.types';
import type { Transaction } from 'types/transactions.types';
import { AvailableCountries, VATTypeEnum } from 'types/users.types';
import { type DEVehicle, type VehiclesOption } from 'types/vehicle.types';
import { getTracingEvents } from 'utils/amplitude';
import * as expensesEvents from 'utils/amplitude/events/expenses.amplitude';
import * as RevenuesEvents from 'utils/amplitude/events/revenues.amplitude';
import { viewSuggestedChanges } from 'utils/applySuggestedChanges';
import { REVENUE_CAR_PRIVATE_USE } from 'utils/categories';
import { isExpert, isWeb } from 'utils/constants';
import { currenciesList } from 'utils/currencies';
import { calculateVATAmount } from 'utils/expenses/helper';
import formatMoney from 'utils/formatMoney';
import getCurrencyIconPath from 'utils/getCurrencyIconPath';
import { createSentryCustomErrorMsg } from 'utils/helpers';
import {
  ChevronDownIconOutline,
  ChevronUpIconOutline,
  LockClosedIconOutline,
  MinusIcon,
  PlusIcon,
} from 'utils/icons';
import {
  dividBy10,
  divideBy1000,
  evaluateExchangeRateFromAmounts,
  multiplyBy10,
  multiplyBy1000,
  round,
} from 'utils/math';
import { ONE_SECOND } from 'utils/time';
import transactionToOption from 'utils/transactionToOption';
import periodBasedOnDate from 'utils/updatePeriodBasedOnDate';
import { CategoriesPickerControlled } from './Fields/CategoriesPicker';
import CustomPaymentAmount from './Fields/CustomPaymentAmount';
import FieldsArray from './Fields/FieldsArray';
import GermanCarsFormControlled from './Fields/GermanCarsFormControlled';
import RevenueNumber from './Fields/RevenueNumber';
import useRevenuesCategoriesPicker from './Fields/useRevenuesCategoriesPicker';
import WhyZeroVAT from './Fields/WhyZeroVAT';
import RevenueVATCells from './RevenueVATCells.component';
import useFormMeta from './useFormMeta';
import {
  encodeGermanCarsData,
  formatGermanCarsData,
  getGermanCarFuelFactor,
  getInvoiceItemsTotalExclVAT,
  getInvoiceItemsTotalInclVAT,
  getLockStatusBody,
  getRevenueLinkedPaymentsAmount,
  getRevenueLinkedPaymentsOppositeDirectionTotal,
  itemDefaults,
  removeInvalidVATRateGroups,
} from './utils';
import {
  germanCarsCustomSchema,
  userDataValidationSchema,
} from './validationSchema';
import RevenueProjectInvoices from './RevenueProjectInvoices.component';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';
import SegmentedToggle from 'components/molecules/SegmentedToggle.component';
import { UserPlusIcon } from 'utils/icons/figmaIcons';
import {
  useNextInvoiceNumber,
  useValidateInvoiceNumber,
} from 'hooks/Revenues/useInvoiceNumber';
import { EmailSendOutLineIcon } from 'utils/icons/figmaIcons';
import { getBelgianQuickActionsVATPeriod } from '../helpers';
import FilesDownloadList from 'components/organisms/FilesDownloadList';

type FormProps = {
  formAPI: UseFormReturn;
  revenue: Partial<Invoice>;
  generatedDefaultValues: InvoiceFormValues;
  isFormDirty?: boolean;
  onClose: () => void;
  onSubmit: (options?: { prepareToSend?: boolean }) => Promise<void>;
  headerMeta?: ReactNode;
  footerMeta?: ReactNode;
  disabled?: boolean;
  disabledFields?: string[] | '*';
  notDisabledFields?: string[];
  isLocked?: boolean;
  shouldUnitPriceIncludeVATByDefault?: boolean;
  onEditVehicles?: () => void;
  onChangeRevenue?: (id: string) => void;
};

const markdownComponents: ReactMarkdownOptions['components'] = {
  ul: ({ children }) => <ul className="list-disc list-inside">{children}</ul>,
};

const tracingEvents = getTracingEvents({
  onAddGermanCar: expensesEvents.WEBAPP_VEHICLE_ADDEDGERMANCAR,
  onEditGermanCar: expensesEvents.WEBAPP_VEHICLE_EDITEDGERMANCAR,
  onDeleteGermanCar: expensesEvents.WEBAPP_VEHICLE_DELETEDGERMANCAR,
  onChangeCategory: {
    name: RevenuesEvents.WEBAPP_REVENUE_UPDATEDCATEGORY,
    data: (id: string) => ({ id }),
  },
});

const Form: FC<FormProps> = ({
  formAPI,
  onClose,
  revenue,
  generatedDefaultValues,
  headerMeta,
  footerMeta,
  onSubmit,
  disabled,
  disabledFields,
  notDisabledFields,
  isLocked,
  isFormDirty,
  onEditVehicles,
  onChangeRevenue,
  ...rest
}) => {
  const queryClient = useQueryClient();
  const invalidateResources = useInvalidateResources();
  const { t } = useTranslation();

  const customerCountry = useCustomerCountry() as AvailableCountries;

  const {
    errors: formErrors,
    dirtyFields,
    isSubmitting,
    isValid,
  } = formAPI.formState;
  const formValues = formAPI.watch() as any;

  const hasErrors = !isValid;

  const itemsController = useFieldArray({
    name: 'items',
    control: formAPI.control,
  });

  const priorityDate = useFeatures('period.priority_date')({
    documentDate: formValues.invoiceDate,
    paymentDate: formValues.paymentDate,
    section: Section.revenues,
    type: formValues.type,
  });

  const priorityDateKey = useFeatures('period.priority_date_key')({
    section: Section.revenues,
    type: formValues.type,
  });

  const { getFormMeta, getItemMeta, getSuggestedChanges } = useFormMeta();

  const formMeta = getFormMeta(formValues);

  useEffect(() => {
    viewSuggestedChanges(
      formValues,
      getSuggestedChanges(formValues),
      formAPI.setValue,
      true,
    );
  }, [formValues]);

  const defaultVATRate = useFeatures('revenue.default_vat_rate');

  const VATRatesAPI = useGetRevenuesVATRates(formValues.invoiceDate);

  const VATRates = VATRatesAPI.rates.map((value: number) => ({
    label: `${(value * 100).toFixed()}%`,
    value,
  }));

  const documentVATReturnFrequency = formAPI.getValues(
    'user.VATReturnFrequency',
  ) as VATReturnFrequency;

  const allItems = (formValues.items || []) as InvoiceItem[];

  const totalAmountExclVAT = getInvoiceItemsTotalExclVAT(formValues.items);

  const totalAmountInclVAT = getInvoiceItemsTotalInclVAT(formValues.items);

  const presetsOptions = useMemo(
    () =>
      revenueAlternativeTransactionsOptions.map((option) => ({
        ...option,
        label: t(option.label),
      })),
    [t],
  );

  const periodOptions = useMemo(
    () => createPeriodsOptions(priorityDate, documentVATReturnFrequency),
    [priorityDate, documentVATReturnFrequency],
  );

  const updatePeriodBasedOnDate = (date: string | null) => {
    const result = periodBasedOnDate(date, documentVATReturnFrequency);

    formAPI.setValue('period', result, { shouldValidate: true });
  };

  const horizontalFieldLabelClass = formMeta.helpers.isGenerated
    ? ''
    : formMeta.fields.paymentAmount.isHidden
    ? 'w-[120px]'
    : 'w-[160px]';

  useHighlighTaxCheckField(revenue as Partial<IRevenue>);

  const isReviewed = formValues.accountantReview?.reviewStatus === 'reviewed';

  const { isFetching: loadingNextNumber } = useNextInvoiceNumber({
    docType: formValues.type!,
    docDate: formValues.invoiceDate,
    enabled:
      !revenue.revenueNumber && !formMeta.fields.revenueNumber.isDisabled,
    onSuccess: (value) => {
      formAPI.setValue('revenueNumber', value);
    },
  });

  const { isFetching: loadingValidateNumber } = useValidateInvoiceNumber({
    enabled: !formMeta.helpers.isSelfBilling,
    docType: formValues.type!,
    defaultNumber: revenue.revenueNumber,
    nextNumber: formValues.revenueNumber,
    onValid: (value) => {
      formAPI.setValue('_invalidDocumentNumber', null);
      formAPI.trigger('revenueNumber');
    },
    onInvalid: (value) => {
      formAPI.setValue('_invalidDocumentNumber', value);
      formAPI.trigger('revenueNumber');
    },
  });

  const hasTransactions = !!useHasTransactions().data;

  const { invoiceDate, customType, customData, user } = formValues;
  const customTypeData = { invoiceDate, customType, customData, user };

  const germanCarsQueryEnabled =
    !isLocked && formMeta.helpers.isGermanBusinessCar;

  const germanCarsEncodedFormData = germanCarsQueryEnabled
    ? encodeGermanCarsData(customTypeData)
    : null;

  const germanCarsEncodedDefaultData = useMemo(() => {
    if (!germanCarsQueryEnabled) return null;
    return encodeGermanCarsData(generatedDefaultValues);
  }, [generatedDefaultValues, germanCarsQueryEnabled]);

  const germanCarComputedItemsAPI = useSuperQuery({
    queryKey: [
      RevenuesCacheKeys.GermanCarComputedItems,
      useId(),
      germanCarsEncodedFormData,
    ],
    queryFn: () => {
      const encodedDefaultData = germanCarsEncodedDefaultData;

      if (germanCarsEncodedFormData != null) {
        if (germanCarsEncodedFormData === encodedDefaultData) {
          return Promise.resolve(generatedDefaultValues.items || []);
        }
      }

      const formatedCustomData = formatGermanCarsData(customTypeData);

      const result = germanCarsCustomSchema
        .concat(userDataValidationSchema)
        .validate(
          {
            invoiceDate: formatedCustomData.invoiceDate,
            ...formatedCustomData.customData,
            ...formatedCustomData.user,
          },
          {
            allowUnknown: true,
          },
        );

      if (result.error) return Promise.resolve([]);
      return getFormCustomData(formatedCustomData)
        .then(({ data }) => data.items)
        .catch(() => []);
    },
    debounceTime: ONE_SECOND * 0.5,
    enabled: germanCarsQueryEnabled,
    cacheTime: 0,
    onSuccess: (items) => {
      formAPI.setValue('items', items);
    },
  });

  const shouldUnitPriceIncludeVATByDefault =
    rest.shouldUnitPriceIncludeVATByDefault ||
    formMeta.helpers.shouldUnitPriceIncludeVATByDefault;

  const lockStatusParams = getLockStatusBody(formValues);
  const lockByTaxesStatusAPI = useSuperQuery<LockStatusTypeCatgoriesed>({
    queryKey: [RevenuesCacheKeys.TaxLock, useId(), !isLocked, lockStatusParams],
    queryFn: () => getLockStatus(lockStatusParams, customerCountry),
    cacheTime: 0,
    staleTime: 0,
    debounceTime: ONE_SECOND * 0.5,
    enabled: !isLocked,
  });

  const isFieldDisabled = useMemo(() => {
    const disabled: Record<string, boolean> = {};
    const notDisabled: Record<string, boolean> = {};
    if (Array.isArray(disabledFields)) {
      disabledFields.forEach((key) => (disabled[key] = true));
    }
    notDisabledFields?.forEach((key) => (notDisabled[key] = true));

    return (key: string) => {
      if (notDisabled[key]) return false;
      if (disabled[key] || disabledFields === '*') return true;
      return false;
    };
  }, [disabledFields, notDisabledFields]);

  const statusDisabledOptions: Record<string, boolean | undefined> =
    useFeatures('revenue.getStatusDisabledOptions')(isLocked);

  const onChangeTransactions = (selected: any) => {
    const options = selected as any[];
    const latestOption = last(options);
    if (latestOption?.preset) {
      formAPI.setValue('paymentType', latestOption.value, {
        shouldDirty: true,
      });
      formAPI.setValue('status', RevenueStatus.paid, {
        shouldDirty: true,
      });
      return formAPI.setValue('transactions', [], { shouldDirty: true });
    }
    formAPI.setValue('paymentType', null, { shouldDirty: true });
    if (isEmpty(options))
      return formAPI.setValue('transactions', [], { shouldDirty: true });
    formAPI.setValue(
      'transactions',
      options.map(({ transaction }) => transaction),
      { shouldDirty: true },
    );
    formAPI.setValue('status', RevenueStatus.paid, {
      shouldDirty: true,
    });
    const { paymentDate } = getFormMeta(formAPI.getValues() as any).fields;
    if (paymentDate.isApplicable && paymentDate.value == null) {
      if (latestOption?.transaction?.valueDate) {
        formAPI.setValue(
          paymentDate.name,
          dayjs(latestOption.transaction.valueDate).format('YYYY-MM-DD'),
          { shouldDirty: true },
        );
      }
    }
  };

  const renderTransactions = () =>
    formMeta.fields.transactions.isHidden ? null : (
      <>
        {!formMeta.helpers.hasLinkedPayments && (
          <SuggestedTransaction
            data={{
              amount:
                totalAmountInclVAT * (formMeta.helpers.isCreditNote ? -1 : 1),
              date: formValues.invoiceDate,
              currency: formValues.currency,
              communication:
                revenue.communication || revenue.guessedCommunication,
              documentNumber: formValues.revenueNumber,
              name: formValues.client?.name,
            }}
            onAccept={(transaction) => {
              onChangeTransactions([transactionToOption(transaction)]);
            }}
          />
        )}
        <FieldConnector
          name="transactions"
          disabled={(name) => isFieldDisabled(name)}
          label={
            hasTransactions
              ? t('revenue.link_payment.with_transactions')
              : t('revenue.link_payment.without_transactions')
          }
        >
          {({ field: { onChange, ...field } }) => {
            return (
              <PaymentLinkSelect
                tracing={{
                  onMenuOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDLINKPAYMENT,
                  onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDLINKPAYMENT,
                }}
                {...field}
                value={((field.value || []) as Transaction[]).map((t) => t._id)}
                cacheKey={revenue._id || 'revenue_payments_list'}
                optionsDict={getOptionsDict(
                  formValues.transactions?.map(transactionToOption),
                )}
                onChange={onChangeTransactions}
                isDisabled={field.disabled}
                fixedOptions={presetsOptions}
                renderLabelContent={(selected) => {
                  if (!hasTransactions) return null;
                  const selectedPreset = presetsOptions.find(
                    (option) => formValues.paymentType === option.value,
                  );
                  if (!selected?.length && !selectedPreset) return null;
                  return (
                    <div className="mt-3">
                      {selectedPreset?.value === 'otherBank' ||
                      selectedPreset?.value === 'cash' ? (
                        <LinkedItemsProgress
                          type="revenues"
                          total={100}
                          matchedAmount={100}
                        />
                      ) : (
                        <LinkedItemsProgress
                          type="revenues"
                          total={
                            (formValues.currency != null &&
                            formValues.currency !== CurrenciesEnum.EUR
                              ? (formValues.paymentAmount || 0) / 1000
                              : totalAmountInclVAT) +
                            getRevenueLinkedPaymentsOppositeDirectionTotal(
                              selected,
                              formMeta.helpers.isCreditNote,
                            )
                          }
                          matchedAmount={getRevenueLinkedPaymentsAmount(
                            selected,
                            formMeta.helpers.isCreditNote,
                          )}
                        />
                      )}
                    </div>
                  );
                }}
                renderSelected={(selected) => {
                  const selectedPreset = presetsOptions.find(
                    (option) => formValues.paymentType === option.value,
                  );
                  if (!selected?.length && !selectedPreset) return null;
                  return (
                    <div className="flex flex-col mt-2">
                      {selectedPreset && (
                        <TransactionOption2
                          className="p-4 bg-primary-25 border border-primary-100 rounded-lg"
                          isDisabled={field.disabled}
                          transaction={{
                            counterPartyName: selectedPreset.label,
                          }}
                          onDelete={() => {
                            formAPI.setValue('paymentType', null, {
                              shouldDirty: true,
                            });
                          }}
                        />
                      )}
                      {selected?.map((option: any) => (
                        <TransactionOption2
                          key={option.value}
                          transaction={option.transaction}
                          isDisabled={field.disabled}
                          className="p-4 bg-primary-25 border-b border-l border-r border-primary-100 first:rounded-t-lg first:border-t last:rounded-b-lg"
                          onDelete={() =>
                            onChangeTransactions(
                              selected.filter(
                                (selectedOption: any) =>
                                  selectedOption.value !== option.value,
                              ),
                            )
                          }
                        />
                      ))}
                    </div>
                  );
                }}
              />
            );
          }}
        </FieldConnector>
      </>
    );

  const renderPeriod = () => (
    <>
      {!formMeta.fields.period.isHidden && (
        <>
          <FieldConnector
            name="period"
            disabled={isFieldDisabled}
            onShow={() => {
              if (formValues.period != null) return;
              updatePeriodBasedOnDate(priorityDate);
            }}
            errorKeys={[
              'period',
              'period.year',
              'period.month',
              'period.quarter',
            ]}
            label={t('fields.vat_declaration.label')}
            helper={<PeriodHelper />}
            loading={lockByTaxesStatusAPI.isLoading}
          >
            {({ field }) => (
              <Select
                tracing={{
                  onMenuOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDPERIOD,
                  onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDPERIOD,
                }}
                menuPlacement="top"
                options={periodOptions}
                formatOptionLabel={(option, meta) => {
                  if (meta.context !== 'menu') return option.label;
                  return (
                    <div className="flex items-center justify-between">
                      <span>{option.label}</span>

                      {option.isLocked && (
                        <LockClosedIconOutline className="w-5 h-5 text-primary-400" />
                      )}
                    </div>
                  );
                }}
                placeholder=""
                {...field}
                isDisabled={field.disabled}
                value={
                  field.value
                    ? periodToValue(field.value, documentVATReturnFrequency)
                    : null
                }
                onChange={(option: any) => field.onChange(option.period)}
              />
            )}
          </FieldConnector>
        </>
      )}
      {!!lockByTaxesStatusAPI.data?.period && (
        <InvoiceSubmitLockedByTaxes
          type={formValues.type!}
          fieldSlug={'period'}
          taxLockReasons={lockByTaxesStatusAPI.data.period}
        />
      )}
    </>
  );

  const renderOrderReference = () => (
    <FieldConnector
      name="orderReference"
      disabled={(name) =>
        formMeta.fields.orderReference.isDisabled || isFieldDisabled(name)
      }
      label={t('invoice.field.order_reference_po_number.label')}
      helper={
        <Helper>
          <Helper.Paragraph>
            {t('invoice.field.order_reference.helper')}
          </Helper.Paragraph>
        </Helper>
      }
    >
      {({ field }) => (
        <FreeTextInput
          {...field}
          tracing={{
            onFocus: RevenuesEvents.WEBAPP_REVENUE_CLICKEDORDERREFERENCE,
          }}
        />
      )}
    </FieldConnector>
  );

  const isCarPrivateUseCategoryDisabled =
    !formMeta.helpers.isGermanBusinessCar && allItems.length > 1;

  const pickerController = useRevenuesCategoriesPicker({
    defaultTaxPeriod: revenue.taxPeriod,
    type: formValues.type,
    taxPeriod: formValues.taxPeriod,
    helpers: {
      [REVENUE_CAR_PRIVATE_USE]: (
        <Helper>
          <Helper.Title>
            {t('de.revenue.private_usage_business_car')}
          </Helper.Title>
          <Helper.Paragraph>
            {t('de.revenue.private_usage_business_car.helper')}
          </Helper.Paragraph>
        </Helper>
      ),
    },
    disabledCategories: {
      [REVENUE_CAR_PRIVATE_USE]: isCarPrivateUseCategoryDisabled,
    },
    tooltips: {
      [REVENUE_CAR_PRIVATE_USE]: isCarPrivateUseCategoryDisabled
        ? t('one_item_categories.tooltip')
        : null,
    },
    tracing: {
      onOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDCATEGORY,
      // onChange: handled in itemsCategoryPickerOnChange
    },
  });

  const itemsCategoryPickerOnChange = (
    itemIndex: number,
    category: any,
    taxPeriod: any,
  ) => {
    if (taxPeriod) {
      formAPI.setValue('taxPeriod', taxPeriod);
    }

    if (category.customType) {
      formAPI.setValue('customType', category.customType);
      formAPI.setValue('customData.customCategory', category);
      formAPI.setValue('customData.customCategoryId', category.id, {
        shouldValidate: true,
      });
      itemsController.remove();
      return tracingEvents.onChangeCategory(category.id);
    }

    formAPI.setValue('items.' + itemIndex + '.categoryId', category.id, {
      shouldValidate: true,
      shouldDirty: true,
    });

    formAPI.setValue('items.' + itemIndex + '.category', category);
    tracingEvents.onChangeCategory(category.id);
  };

  const currencyRate = formValues.paymentAmount
    ? +evaluateExchangeRateFromAmounts(
        multiplyBy1000(totalAmountInclVAT),
        formValues.paymentAmount,
      )
    : 1;

  useRevenueAiMetadata(formValues);

  const [priceListModalContent, priceListModalAPI] = useModal({
    name: 'price-list-modal',
    width: 'md',
  });

  const priceListEditModalRef = useRef<FormSlideOverManagerApi<ItemInput>>();

  const handlePriceItemSelect = (item: PriceItem, itemIndex: number) => {
    const getName = (name: string) => `items.${itemIndex}.${name}`;

    formAPI.setValue(getName('name'), item.name ?? '');
    formAPI.setValue(
      getName('unitAmountExclVAT'),
      item.unitAmountExclVAT ?? null,
    );
    formAPI.setValue(getName('unitAmountInclVAT'), null); // PriceItem doesn't have unitAmountInclVAT
    formAPI.setValue(getName('VATRate'), item.VATRate ?? null);
    formAPI.setValue(getName('categoryId'), item.category?.id ?? null);
    formAPI.setValue(getName('category'), item.category ?? null);
    formAPI.setValue(getName('unit'), item.unit ?? 'items');
    formAPI.setValue(getName('_priceItemId'), item._id ?? null);
    formAPI.setValue(getName('_priceItem'), item ?? null);
    formAPI.setValue(getName('addToPriceList'), false); // Reset addToPriceList when selecting from price list
    formAPI.setValue(getName('updateOnPriceList'), false); // Reset updateOnPriceList when selecting from price list

    priceListModalAPI.close();
  };

  const openPriceListEditModalWithClose = (id: string) => {
    priceListModalAPI.close();
    setTimeout(() => {
      priceListEditModalRef.current?.open(id);
    }, 0);
  };

  const openPriceListModal = (itemIndex: number) => {
    priceListModalAPI.open(() => (
      <div className="h-[30rem] flex flex-col">
        <h2 className="text-main text-2xl mb-4 font-avenir font-bold">
          {t('priceList.title')}
        </h2>
        <p className="text-main text-sm mb-4">
          {t('invoice.form.add_item_from_catalog')}
        </p>
        <ItemSearchField
          currency={formValues.currency}
          onItemSelect={(item) => handlePriceItemSelect(item, itemIndex)}
          onEdit={openPriceListEditModalWithClose}
          categoryRevenueTypes={InvoiceTypeEnum.OTHER_REVENUE}
        />
      </div>
    ));
  };

  const afterPriceListChange = () => {
    invalidateResources('priceItems');
  };

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const showChatBtn = useMemo(
    () =>
      shouldShowTaxAdvisor
        ? { showTaxAdvisorBtn: true }
        : { showIntercomBtn: true },
    [shouldShowTaxAdvisor],
  );

  return (
    <FormProvider {...formAPI}>
      {pickerController.api.content}
      <PriceListFormSlideOver
        apiRef={priceListEditModalRef}
        afterSubmit={afterPriceListChange}
        afterDelete={afterPriceListChange}
      />
      <form className="relative">
        <div className="flex flex-col gap-4 px-6 pt-10 pb-8 bg-[#FCFBFE] border-b border-primary-100">
          {headerMeta}

          {!formMeta.fields.status.isHidden && (
            <FieldConnector
              name="status"
              disabled={(name) =>
                isFieldDisabled(name) || formMeta.fields.status.isDisabled
              }
              label={t('status')}
            >
              {({ field }) => {
                const isSelfBilling = formMeta.helpers.isSelfBilling;
                return (
                  <SegmentedToggle
                    tracing={{
                      onChange: {
                        name: RevenuesEvents.WEBAPP_REVENUE_UPDATEDSTATUS,
                        data: (value: string) => ({ value, isSelfBilling }),
                      },
                    }}
                    disabled={field.disabled}
                    value={formValues.status}
                    onChange={field.onChange}
                    options={[
                      {
                        label: isSelfBilling
                          ? t('invoice.field.self_billing.rejected')
                          : t('invoice.field.not-sent'),
                        value: InvoiceStatusEnum.notSent,
                        isDisabled:
                          statusDisabledOptions[InvoiceStatusEnum.notSent],
                        selectedClassName: isSelfBilling ? 'text-red-600' : '',
                      },
                      {
                        label: isSelfBilling
                          ? t('invoice.field.self_billing.accepted')
                          : t('invoice.field.sent'),
                        value: InvoiceStatusEnum.sent,
                        isDisabled:
                          statusDisabledOptions[InvoiceStatusEnum.sent],
                        selectedClassName: isSelfBilling
                          ? 'text-yellow-600'
                          : '',
                      },
                      {
                        label: isSelfBilling
                          ? t('invoice.field.self_billing.paid')
                          : t('invoice.field.paid'),
                        value: InvoiceStatusEnum.paid,
                        isDisabled:
                          statusDisabledOptions[InvoiceStatusEnum.paid],
                      },
                    ]}
                  />
                );
              }}
            </FieldConnector>
          )}

          {formMeta.helpers.shouldShowSelfBillingRejectedWarning && (
            <Alert
              type="error"
              name="self billing rejected"
              dismissable
              dismissId={AlertIdsEnum.revenuesSelfBillingRejected}
              title={t('invoice.field.self_billing.rejected.warning.title')}
              description={t(
                'invoice.field.self_billing.rejected.warning.text',
              )}
            />
          )}

          {formMeta.helpers.shouldShowSelfBillingAcceptedWarning && (
            <Alert
              type="error"
              name="self billing accepted"
              dismissable
              dismissId={AlertIdsEnum.revenuesSelfBillingAccepted}
              description={t(
                `credit_note.field.self_billing.rejected.warning.text.${customerCountry}`,
              )}
            />
          )}

          {!formMeta.fields.orderReference.isHidden &&
            formMeta.helpers.isGenerated &&
            renderOrderReference()}

          {!formMeta.fields.currency.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="currency"
              disabled={(name) =>
                isFieldDisabled(name) || formMeta.fields.currency.isDisabled
              }
              label={t('invoice.field.currency')}
            >
              {({ field: { onChange, ...rest } }) => (
                <Select
                  tracing={{
                    onMenuOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDCURRENCY,
                    onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDCURRENCY,
                  }}
                  isDisabled={rest.disabled}
                  isSearchable
                  placeholder={t('invoice.field.select')}
                  onChange={(option: any) => onChange(option.value)}
                  options={currenciesList.map((option) => ({
                    ...option,
                    label: t(option.slug),
                  }))}
                  formatOptionLabel={(option) => {
                    const iconPath = getCurrencyIconPath(option.value);
                    return (
                      <div className="flex items-center gap-2">
                        {!!iconPath && (
                          <img src={iconPath} className="w-6 h-6" />
                        )}
                        {option.label}
                      </div>
                    );
                  }}
                  {...rest}
                />
              )}
            </FieldConnector>
          )}

          {!formMeta.fields.paymentAmount.isHidden &&
            (formMeta.helpers.isGenerated ? (
              <FieldConnector
                labelClassName={horizontalFieldLabelClass}
                labelWrapperClassName={horizontalFieldLabelClass}
                name="paymentAmount"
                disabled
                helper={<PaymentAmountHelper />}
                label={`${t('invoice.field.paymentAmount')} (${t(
                  'invoice.form.exchange_rate',
                )} ${currencyRate})`}
              >
                {({ field: { value, onChange, ...field } }) => (
                  <PaymentAmount
                    disabled
                    value={value == null ? null : divideBy1000(value)}
                    onChange={() => null}
                  />
                )}
              </FieldConnector>
            ) : (
              <FieldConnector
                horizontal
                labelClassName={horizontalFieldLabelClass}
                labelWrapperClassName={horizontalFieldLabelClass}
                name="paymentAmount"
                disabled={(name) =>
                  isFieldDisabled(name) ||
                  formMeta.fields.paymentAmount.isDisabled
                }
                helper={<PaymentAmountHelper />}
                label={`${t('invoice.field.paymentAmount')}`}
                subLabel={`${
                  formValues.paymentAmount
                    ? `(${t(
                        'invoice.form.exchange_rate',
                      )} ${evaluateExchangeRateFromAmounts(
                        multiplyBy1000(totalAmountInclVAT),
                        formValues.paymentAmount,
                      )})`
                    : ''
                }`}
              >
                {({ field: { value, onChange, ...field } }) => (
                  <CustomPaymentAmount
                    tracing={{
                      onFocus:
                        RevenuesEvents.WEBAPP_REVENUE_CLICKEDPAYMENTAMOUNT,
                    }}
                    {...field}
                    value={value == null ? null : divideBy1000(value)}
                    onChange={(v) =>
                      onChange(v == null ? null : multiplyBy1000(v))
                    }
                    onNewCurrencyRate={(v) =>
                      formAPI.setValue(field.name, multiplyBy1000(v), {
                        shouldValidate: true,
                      })
                    }
                    params={{
                      clientId: formValues.clientId,
                      items:
                        totalAmountExclVAT != null
                          ? [
                              {
                                quantity: 1000,
                                unitAmountExclVAT:
                                  multiplyBy1000(totalAmountExclVAT),
                              },
                            ]
                          : [],
                      invoiceDate: formValues.invoiceDate,
                      type: formValues.type,
                      options: { currency: formValues.currency },
                    }}
                  />
                )}
              </FieldConnector>
            ))}

          {!formMeta.fields.revenueNumber.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="revenueNumber"
              disabled={(name) =>
                formMeta.fields.revenueNumber.isDisabled ||
                isFieldDisabled(name)
              }
              label={t('invoices.cells.invoice_number')}
            >
              {({ field }) => (
                <FreeTextInput
                  tracing={{
                    onFocus: RevenuesEvents.WEBAPP_REVENUE_CLICKEDREVENUENUMBER,
                  }}
                  {...field}
                  loading={loadingNextNumber || loadingValidateNumber}
                />
              )}
            </FieldConnector>
          )}

          {!formMeta.fields.invoiceDate.isHidden && (
            <>
              <FieldConnector
                horizontal={!formMeta.helpers.isGenerated}
                labelClassName={horizontalFieldLabelClass}
                name="invoiceDate"
                disabled={(name) =>
                  formMeta.fields.invoiceDate.isDisabled ||
                  isFieldDisabled(name)
                }
                label={t('invoice.field.issued')}
                loading={lockByTaxesStatusAPI.isLoading}
              >
                {({ field: { value, onChange, ...field } }) => (
                  <DatePicker
                    tracing={{
                      onCalendarOpen:
                        RevenuesEvents.WEBAPP_REVENUE_CLICKEDINVOICEDATE,
                      onChange:
                        RevenuesEvents.WEBAPP_REVENUE_UPDATEDINVOICEDATE,
                    }}
                    {...field}
                    selected={value}
                    onChange={(value) => {
                      const date = value
                        ? dayjs(value).format('YYYY-MM-DD')
                        : null;
                      onChange(date);
                      if (
                        priorityDateKey === field.name &&
                        !formMeta.fields.period.isHidden
                      ) {
                        updatePeriodBasedOnDate(date);
                      }
                      if (
                        formMeta.helpers.canAutoPopulateDeliveryDateAndPeriod &&
                        !formMeta.fields.deliveryDate.isHidden &&
                        !formMeta.fields.deliveryDate.isDisabled &&
                        !dirtyFields['deliveryDate']
                      ) {
                        formAPI.setValue('deliveryDate', date);
                      }
                    }}
                  />
                )}
              </FieldConnector>
            </>
          )}

          {customerCountry === AvailableCountries.belgium &&
            !!lockByTaxesStatusAPI.data?.date && (
              <InvoiceSubmitLockedByTaxes
                type={formValues.type}
                fieldSlug={'invoice_date'}
                taxLockReasons={lockByTaxesStatusAPI.data.date}
              />
            )}

          {formMeta.helpers.shouldShowInvoiceDateWarning && (
            <Alert
              type="info"
              name="invoice date in the future"
              {...showChatBtn}
              description={t('warning_bubble.document_date_in_the_future')}
            />
          )}

          {!formMeta.fields.dueDate.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="dueDate"
              disabled={(name) =>
                formMeta.fields.dueDate.isDisabled || isFieldDisabled(name)
              }
              label={t('invoice.field.due_date')}
            >
              {({ field: { value, onChange, ...field } }) => (
                <DatePicker
                  tracing={{
                    onCalendarOpen:
                      RevenuesEvents.WEBAPP_REVENUE_CLICKEDDUEDATE,
                    onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDDUEDATE,
                  }}
                  {...field}
                  filterDate={(date: Date) =>
                    formValues.invoiceDate
                      ? !dayjs(date).isBefore(dayjs(formValues.invoiceDate))
                      : true
                  }
                  selected={value}
                  onChange={(value) => {
                    onChange(value ? dayjs(value).format('YYYY-MM-DD') : null);
                  }}
                />
              )}
            </FieldConnector>
          )}

          {(!formMeta.fields.deliveryDate.isHidden ||
            !formMeta.fields.deliveryPeriod.isHidden) && (
            <FakeField
              horizontal
              labelClassName={horizontalFieldLabelClass}
              label={
                <Menu
                  contentWrapperClassName="!w-max"
                  buttonProps={{
                    className: 'flex',
                    disabled:
                      (formMeta.fields.deliveryPeriod.isDisabled ||
                        isFieldDisabled('deliveryPeriod')) &&
                      (formMeta.fields.deliveryDate.isDisabled ||
                        isFieldDisabled('deliveryDate')),
                  }}
                  trigger={(api) => (
                    <Button
                      asDefault
                      className="flex items-center gap-0.5 text-init"
                    >
                      {formValues.showDeliveryPeriod
                        ? formMeta.helpers.isCustomerDE
                          ? t('invoice.field.delivery_period')
                          : t('invoice.field.service_period')
                        : formMeta.helpers.isCustomerDE
                        ? t('invoice.field.delivery_date')
                        : t('invoice.field.service_date')}
                      {api.open ? (
                        <ChevronUpIconOutline className="w-4 h-4" />
                      ) : (
                        <ChevronDownIconOutline className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                >
                  <Menu.Group>
                    <Menu.Button
                      onClick={() => {
                        if (!formAPI.getValues('showDeliveryPeriod')) return;
                        formAPI.setValue('showDeliveryPeriod', false);
                        formAPI.setValue(
                          'deliveryDate',
                          formMeta.helpers.canAutoPopulateDeliveryDateAndPeriod
                            ? formValues['invoiceDate'] || null
                            : null,
                        );
                        formAPI.setValue('deliveryPeriod', null);
                      }}
                    >
                      {formMeta.helpers.isCustomerDE
                        ? t('invoice.field.delivery_date')
                        : t('invoice.field.service_date')}
                    </Menu.Button>
                    <Menu.Button
                      onClick={() => {
                        if (formAPI.getValues('showDeliveryPeriod')) return;
                        formAPI.setValue('showDeliveryPeriod', true);
                        formAPI.setValue('deliveryDate', null);
                        formAPI.setValue('deliveryPeriod', null);
                      }}
                    >
                      {formMeta.helpers.isCustomerDE
                        ? t('invoice.field.delivery_period')
                        : t('invoice.field.service_period')}
                    </Menu.Button>
                  </Menu.Group>
                </Menu>
              }
            >
              {() => {
                if (!formMeta.fields.deliveryPeriod.isHidden) {
                  return (
                    <>
                      <FieldConnector
                        key="deliveryPeriod"
                        name="deliveryPeriod"
                        forceHideError
                        disabled={(name) =>
                          formMeta.fields.deliveryPeriod.isDisabled ||
                          isFieldDisabled(name)
                        }
                      >
                        {({ field: { value, onChange, ...field } }) => (
                          <DatePicker
                            {...field}
                            selectsRange={true}
                            startDate={value?.start}
                            endDate={value?.end}
                            onChange={(value: any) => {
                              const range = value as [Date | null, Date | null];
                              if (!range[0] && !range[1]) return onChange(null);
                              onChange({
                                start: range[0]
                                  ? dayjs(range[0]).format('YYYY-MM-DD')
                                  : null,
                                end: range[1]
                                  ? dayjs(range[1]).format('YYYY-MM-DD')
                                  : null,
                              });
                            }}
                            onChangeRaw={(event) => {
                              const inputValue = event.currentTarget.value;
                              if (!inputValue) return;
                              const range = textToDateRange(inputValue);
                              if (!range) return onChange(null);
                              onChange({
                                start: dayjs(range[0]).format('YYYY-MM-DD'),
                                end: dayjs(range[1]).format('YYYY-MM-DD'),
                              });
                            }}
                          />
                        )}
                      </FieldConnector>
                      <FieldErrorManager
                        key="deliveryPeriod-error"
                        name="deliveryPeriod"
                        className="col-start-2 mt-1"
                      />
                    </>
                  );
                }
                return (
                  <>
                    <FieldConnector
                      key="deliveryDate"
                      name="deliveryDate"
                      forceHideError
                      disabled={(name) =>
                        formMeta.fields.deliveryDate.isDisabled ||
                        isFieldDisabled(name)
                      }
                    >
                      {({ field: { value, onChange, ...field } }) => (
                        <DatePicker
                          tracing={{
                            onCalendarOpen:
                              RevenuesEvents.WEBAPP_REVENUE_CLICKEDDELIVERYDATE,
                            onChange:
                              RevenuesEvents.WEBAPP_REVENUE_UPDATEDDELIVERYDATE,
                          }}
                          {...field}
                          selected={value}
                          onChange={(value) => {
                            onChange(
                              value ? dayjs(value).format('YYYY-MM-DD') : null,
                            );
                          }}
                        />
                      )}
                    </FieldConnector>
                    <FieldErrorManager
                      key="deliveryDate-error"
                      name="deliveryDate"
                      className="col-start-2 mt-1"
                    />
                  </>
                );
              }}
            </FakeField>
          )}

          {formMeta.helpers.isGenerated && renderTransactions()}

          {!formMeta.fields.paymentDate.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="paymentDate"
              disabled={isFieldDisabled}
              label={t('invoice.field.payment_date')}
              loading={lockByTaxesStatusAPI.isLoading}
            >
              {({ field: { value, onChange, ...field } }) => (
                <DatePicker
                  tracing={{
                    onCalendarOpen:
                      RevenuesEvents.WEBAPP_REVENUE_CLICKEDPAYMENTDATE,
                    onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDPAYMENTDATE,
                  }}
                  {...field}
                  // filterDate={(date: Date) =>
                  //   formValues.invoiceDate
                  //     ? !dayjs(date).isBefore(dayjs(formValues.invoiceDate))
                  //     : true
                  // }
                  selected={value}
                  onChange={(value) => {
                    const date = value
                      ? dayjs(value).format('YYYY-MM-DD')
                      : null;
                    onChange(date);
                    if (
                      priorityDateKey === field.name &&
                      !formMeta.fields.period.isHidden
                    ) {
                      updatePeriodBasedOnDate(date);
                    }
                  }}
                />
              )}
            </FieldConnector>
          )}
          {customerCountry === AvailableCountries.germany &&
            !!lockByTaxesStatusAPI.data?.date && (
              <InvoiceSubmitLockedByTaxes
                type={formValues.type}
                fieldSlug={'payment_date'}
                taxLockReasons={lockByTaxesStatusAPI.data.date}
              />
            )}

          {formMeta.helpers.isGenerated && renderPeriod()}

          {!formMeta.fields.otherRevenueClient.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="client"
              disabled={isFieldDisabled}
              label={`${t('invoice.field.client')} (${t(
                'field.optional.tag',
              )})`}
            >
              {({ field: { onChange, value, ...rest } }) => (
                <Select
                  tracing={{
                    onMenuOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDCLIENT,
                    onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDCLIENT,
                  }}
                  mode="asyncOptionsPaginated"
                  asyncSearch
                  creatable
                  isDisabled={rest.disabled}
                  themeOptions={{
                    hideDropdownIndicator: true,
                  }}
                  placeholder={t('invoice.field.select')}
                  value={value?.name}
                  onChange={(option: any) => {
                    const client = {
                      name: option?.value || undefined,
                      location: 'local',
                    };
                    onChange(client);
                  }}
                  onFetch={(name, page) =>
                    getSuggestedClients({ name, page }).then(
                      ({ data, paging }) => ({
                        paging,
                        data: data.map((client: any) => ({
                          label: client.name,
                          value: client.name,
                          client,
                        })),
                      }),
                    )
                  }
                  {...rest}
                />
              )}
            </FieldConnector>
          )}

          {!formMeta.fields.invoiceClient.isHidden && (
            <FieldConnector
              horizontal={!formMeta.helpers.isGenerated}
              labelClassName={horizontalFieldLabelClass}
              name="clientId"
              disabled={isFieldDisabled}
              label={t('invoice.field.client')}
            >
              {({ field: { onChange, value, ...rest } }) => (
                <Select
                  tracing={{
                    onMenuOpen: RevenuesEvents.WEBAPP_REVENUE_CLICKEDCLIENT,
                    onChange: RevenuesEvents.WEBAPP_REVENUE_UPDATEDCLIENT,
                  }}
                  mode="asyncOptions"
                  isSearchable
                  isDisabled={rest.disabled}
                  cacheKey={RevenuesCacheKeys.RevenueClientFieldOptions}
                  placeholder={t('invoice.field.select')}
                  onChange={(option: any) => {
                    onChange(option.value);
                    formAPI.setValue('client', option.client);
                  }}
                  onFetch={() => getClientsOptions()}
                  value={value}
                  {...rest}
                />
              )}
            </FieldConnector>
          )}

          {!formMeta.fields.invoiceClient.isHidden && !isLocked && (
            <UseRef
              render={(ref) => {
                if (formMeta.helpers.isSelfBilling && formValues.clientId)
                  return null;
                const currentClient = formValues.client;
                const shouldSuggest =
                  !currentClient?.id &&
                  !currentClient?._id &&
                  !!currentClient?.name;
                return (
                  <>
                    <Button
                      structure="text"
                      className="self-start"
                      tracingEvent={RevenuesEvents.WEBAPP_REVENUE_ADDCLIENT}
                      onClick={() => {
                        ref.current?.open(
                          shouldSuggest ? formValues.client : {},
                        );
                      }}
                    >
                      <UserPlusIcon className="w-6 h-6" />
                      {t('invoice.field.add_new_client', {
                        name: shouldSuggest
                          ? `(${t('suggested')}: ${currentClient?.name})`
                          : '',
                      })}
                    </Button>
                    <ClientForm
                      apiRef={ref}
                      afterSubmit={(result) => {
                        queryClient.invalidateQueries([
                          RevenuesCacheKeys.RevenuesClients,
                        ]);
                        queryClient.invalidateQueries([
                          SelectOptionsCacheKeys.Async,
                          RevenuesCacheKeys.RevenueClientFieldOptions,
                        ]);
                        formAPI.setValue('clientId', result._id, {
                          shouldValidate: true,
                        });
                        formAPI.setValue('client', result);
                      }}
                    />
                  </>
                );
              }}
            />
          )}
        </div>

        <div className={formMeta.helpers.isGenerated ? '' : 'mt-6'}>
          {formMeta.helpers.isGermanBusinessCar && (
            <div className="flex flex-col gap-4 px-6 pt-4">
              {!formMeta.fields.customCategoryId.isHidden && (
                <FieldConnector
                  className="flex-1"
                  name={'customData.customCategoryId'}
                  label={t('expense.category')}
                  disabled={isFieldDisabled}
                >
                  {({ field: { value, onChange, ...rest } }) => (
                    <CategoriesPickerControlled
                      {...rest}
                      isDisabled={rest.disabled}
                      value={value}
                      pickerController={pickerController}
                      taxPeriod={formValues.taxPeriod}
                      onChange={(category: any, taxPeriod: any) => {
                        if (taxPeriod) formAPI.setValue('taxPeriod', taxPeriod);

                        if (!category.customType) {
                          onChange(null);
                          formAPI.setValue('customType', null);
                          formAPI.setValue('customData.customCategory', null);
                          return formAPI.setValue('items', [
                            {
                              ...itemDefaults,
                              category,
                              categoryId: category.id,
                            },
                          ]);
                        }

                        onChange(category.id);
                        formAPI.setValue('customData.customCategory', category);
                        formAPI.setValue('customType', category.customType);
                      }}
                    />
                  )}
                </FieldConnector>
              )}

              {!formMeta.fields.usageStartDate.isHidden &&
                !formMeta.fields.usageEndDate.isHidden && (
                  <div className="flex gap-2">
                    <FieldConnector
                      className="flex-1 min-w-0"
                      name={'customData.usageStartDate'}
                      label={t('invoice.field.usage_from')}
                      disabled={isFieldDisabled}
                    >
                      {({ field: { value, ...field } }) => {
                        return (
                          <DatePicker
                            {...field}
                            selected={value}
                            showMonthYearPicker
                            dateFormat="MMMM yyyy"
                            maxDate={
                              formValues.customData?.usageEndDate ||
                              dayjs().endOf('year')
                            }
                            onChange={(value) => {
                              const date = value
                                ? dayjs(value).format('YYYY-MM-DD')
                                : null;
                              field.onChange(date);
                            }}
                            tracing={{
                              onCalendarOpen:
                                RevenuesEvents.WEBAPP_REVENUE_CLICKEDUSAGESTART,
                              onChange:
                                RevenuesEvents.WEBAPP_REVENUE_UPDATEDUSAGESTART,
                            }}
                          />
                        );
                      }}
                    </FieldConnector>
                    <FieldConnector
                      className="flex-1 min-w-0"
                      name={'customData.usageEndDate'}
                      label={t('invoice.field.usage_to')}
                      disabled={isFieldDisabled}
                    >
                      {({ field: { value, ...field } }) => (
                        <DatePicker
                          {...field}
                          selected={value}
                          showMonthYearPicker
                          dateFormat="MMMM yyyy"
                          maxDate={dayjs().endOf('year')}
                          minDate={formValues.customData?.usageStartDate}
                          onChange={(value) => {
                            const date = value
                              ? dayjs(value).format('YYYY-MM-DD')
                              : null;
                            field.onChange(date);
                          }}
                          tracing={{
                            onCalendarOpen:
                              RevenuesEvents.WEBAPP_REVENUE_CLICKEDUSAGEEND,
                            onChange:
                              RevenuesEvents.WEBAPP_REVENUE_UPDATEDUSAGEEND,
                          }}
                        />
                      )}
                    </FieldConnector>
                  </div>
                )}

              {formMeta.helpers.shouldShowUsagePeriodWarning && (
                <Alert
                  type="warning"
                  dismissId={AlertIdsEnum.otherRevenueGermanCarUsage}
                  dismissable
                  name="german car usage more than 1 period"
                  description={t(
                    `invoice.warning_bubble.car_usage_more_than_1_period.${formValues.user?.VATReturnFrequency}`,
                  )}
                />
              )}

              {!formMeta.fields.vehicleId.isHidden && (
                <FieldConnector
                  name={'customData.vehicleId'}
                  label={t('invoice.field.car')}
                  disabled={isFieldDisabled}
                >
                  {({ field: { onChange, ...rest } }) => {
                    const _onChange = (
                      option: VehiclesOption<DEVehicle> | null,
                    ) => {
                      onChange(option?.value);
                      formAPI.setValue('customData.vehicle', option?.vehicle);
                    };
                    return (
                      <CarsSelect
                        {...rest}
                        tracing={{
                          onMenuOpen:
                            RevenuesEvents.WEBAPP_REVENUE_CLICKEDVEHICLE,
                          onChange:
                            RevenuesEvents.WEBAPP_REVENUE_UPDATEDVEHICLE,
                        }}
                        contextId={germanCarsEncodedFormData}
                        defaultContextId={germanCarsEncodedDefaultData}
                        getContextIdBasedOnVehicle={(vehicle: any) => {
                          return encodeGermanCarsData({
                            ...customTypeData,
                            customData: {
                              ...customTypeData.customData,
                              vehicle,
                            },
                          });
                        }}
                        // use the vehicle name as the value label, usefull when the vehicle is deleted
                        // because we still have the vehicle object on the document
                        valueLabel={formValues.customData?.vehicle?.name}
                        isDisabled={rest.disabled}
                        onChange={_onChange}
                        onAddVehicle={(vehicle) => {
                          _onChange(vehicle);
                          onEditVehicles?.();
                          tracingEvents.onAddGermanCar();
                        }}
                        onEditVehicle={(option) => {
                          onEditVehicles?.();
                          if (rest.value === option.value) {
                            _onChange(option);
                          }
                          tracingEvents.onEditGermanCar();
                        }}
                        onDeleteVehicle={(option) => {
                          onEditVehicles?.();
                          if (rest.value === option.value) {
                            _onChange(null);
                          }
                          tracingEvents.onDeleteGermanCar();
                        }}
                        getVehiclesOptions={() =>
                          getVehiclesOptions<DEVehicle>()
                        }
                        deleteVehicle={deleteVehicle}
                        deleteMessage={t('german_car.delete_warning')}
                        renderForm={(props) => (
                          <GermanCarsFormControlled
                            {...props}
                            onSubmit={(values) => {
                              if (props.context === 'add') {
                                return createVehicle(values).then(
                                  props.onSubmit,
                                );
                              }
                              return updateVehicle(values._id, values).then(
                                props.onSubmit,
                              );
                            }}
                          />
                        )}
                      />
                    );
                  }}
                </FieldConnector>
              )}

              {!formMeta.fields.isVehicleUsedForOfficeCommute.isHidden && (
                <FieldConnector
                  name={'customData.isVehicleUsedForOfficeCommute'}
                  className="flex flex-row-reverse gap-2 items-center justify-end"
                  labelClassName="mb-0"
                  label={t('invoice.field.car_commute_to_work')}
                  disabled={isFieldDisabled}
                >
                  {({ field: { value, onChange, disabled } }) => (
                    <Toggle
                      enabled={!!value}
                      onChange={onChange}
                      disabled={disabled}
                      tracing={{
                        onChange:
                          RevenuesEvents.WEBAPP_REVENUE_UPDATEDCARUSEDFOROFFICETOGGLE,
                      }}
                    />
                  )}
                </FieldConnector>
              )}

              {!formMeta.fields.distanceBetweenHomeAndOffice.isHidden && (
                <FieldConnector
                  name={'customData.distanceBetweenHomeAndOffice'}
                  label={t('invoice.field.km_to_work')}
                  disabled={isFieldDisabled}
                >
                  {({ field }) => (
                    <NumberField
                      {...field}
                      format={{ prefix: ' km' }}
                      tracing={{
                        onFocus:
                          RevenuesEvents.WEBAPP_REVENUE_CLICKEDDISTANCEHOMETOOFFICE,
                      }}
                    />
                  )}
                </FieldConnector>
              )}

              {!!allItems.length && (
                <div className="flex gap-2">
                  <FakeField
                    className="flex-1"
                    label={t('invoice.field.vat_rate_amount_incl_vat')}
                    helper={
                      <Helper>
                        <Helper.Title>
                          {t('invoice.business_car.amount_helper.title1')}
                        </Helper.Title>
                        <Helper.Paragraph className="whitespace-pre-wrap">
                          {t('invoice.business_car.amount_helper.text1.temp', {
                            listPrice: formatMoney(
                              formValues.customData?.vehicle?.listPrice || 0,
                            ),
                            numberOfmonths:
                              dayjs(formValues.customData?.usageEndDate).diff(
                                dayjs(formValues.customData?.usageStartDate),
                                'month',
                              ) + 1,
                            totalAmountExclVAT: formatMoney(totalAmountExclVAT),
                            factor: getGermanCarFuelFactor(
                              formValues.customData?.vehicle,
                            ),
                            distance:
                              formValues.customData
                                ?.distanceBetweenHomeAndOffice || 0,
                          })}
                        </Helper.Paragraph>
                        <Helper.Title>
                          {t('invoice.business_car.amount_helper.title2')}
                        </Helper.Title>
                        <Helper.Paragraph className="whitespace-pre-wrap">
                          {t('invoice.business_car.amount_helper.text2')}
                        </Helper.Paragraph>
                      </Helper>
                    }
                  >
                    {() => (
                      <PaymentAmount value={totalAmountInclVAT} disabled />
                    )}
                  </FakeField>
                  <FakeField className="flex-1" label={t('VAT')}>
                    {() => (
                      <PaymentAmount
                        value={totalAmountInclVAT - totalAmountExclVAT}
                        disabled
                      />
                    )}
                  </FakeField>
                </div>
              )}

              {formMeta.helpers.shouldShow1RuleInfo && (
                <Alert
                  type="info"
                  description={t('invoice.info_bubble.1_rule')}
                  renderButtons={(themeClassName) => (
                    <Helper
                      trigger={(openModal) => (
                        <Button
                          asDefault
                          onClick={openModal}
                          className={classNames(
                            'underline text-sm font-semibold',
                            themeClassName,
                          )}
                        >
                          {t('learn_more')}
                        </Button>
                      )}
                    >
                      <Helper.Paragraph className="whitespace-pre-line">
                        {t('german_car.encode_private_usage.helper')}
                      </Helper.Paragraph>
                    </Helper>
                  )}
                />
              )}
            </div>
          )}

          <div className="flex flex-col gap-4 px-6 pt-4">
            {!formMeta.fields.items.isHidden && (
              <FieldConnector name="items">
                {({ name: parentName }) => (
                  <FieldsArray
                    controller={itemsController}
                    name={parentName}
                    showItemsNumber={
                      formMeta.helpers.isItemsComputed ||
                      formValues.items?.length > 1
                    }
                    isItemsRemovable={
                      !isLocked &&
                      !formMeta.helpers.isItemsComputed &&
                      !formMeta.helpers.isSelfBilling &&
                      formValues.items?.length > 1
                    }
                    render={(props) => {
                      const { field, index } = props;
                      const getName = (name: string) =>
                        `${parentName}.${index}.${name}`;
                      const item = formValues.items[index];

                      const itemMeta = getItemMeta(
                        formValues,
                        item,
                        `${parentName}.${index}.`,
                      );

                      const onRateChange = (newRate: any) => {
                        const value = multiplyBy1000(newRate);
                        formAPI.setValue(getName('VATRate'), value);
                      };

                      const onToggleIncludeVAT = (
                        doesUnitPriceIncludeVAT: boolean,
                      ) => {
                        formAPI.setValue(
                          getName('doesUnitPriceIncludeVAT'),
                          doesUnitPriceIncludeVAT,
                        );

                        if (doesUnitPriceIncludeVAT) {
                          formAPI.setValue(
                            getName('unitAmountInclVAT'),
                            formAPI.getValues(getName('unitAmountExclVAT')) ||
                              0,
                            { shouldValidate: true },
                          );
                          return formAPI.setValue(
                            getName('unitAmountExclVAT'),
                            null,
                            { shouldValidate: true },
                          );
                        }
                        formAPI.setValue(
                          getName('unitAmountExclVAT'),
                          formAPI.getValues(getName('unitAmountInclVAT')) || 0,
                          { shouldValidate: true },
                        );
                        formAPI.setValue(getName('unitAmountInclVAT'), null, {
                          shouldValidate: true,
                        });
                      };

                      return (
                        <div
                          key={field.id}
                          className={classNames('flex flex-col gap-4')}
                        >
                          {!itemMeta.fields.name.isHidden && (
                            <FieldConnector
                              name={getName('name')}
                              disabled={(name) =>
                                isFieldDisabled(name) ||
                                itemMeta.fields.name.isDisabled
                              }
                              label={`${t('clients.field.name')}${
                                formMeta.helpers.isOtherRevenue
                                  ? ` (${t('field.optional.tag')})`
                                  : ''
                              }`}
                            >
                              {({ field: { onChange, ...field } }) => (
                                <div className="flex items-center justify-between">
                                  <div className="flex-1 w-full mr-4">
                                    <FreeTextInput
                                      tracing={{
                                        onFocus:
                                          RevenuesEvents.WEBAPP_REVENUE_CLICKEDITEMNAME,
                                      }}
                                      {...field}
                                      disabled={field.disabled}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        onChange(value);

                                        if (
                                          value.trim() &&
                                          !revenue._id &&
                                          formMeta.helpers.isOtherRevenue
                                        ) {
                                          const currentItem =
                                            formAPI.getValues('items')[index];
                                          const isFromPriceList =
                                            !!currentItem._priceItemId &&
                                            !!currentItem._priceItem;

                                          if (!isFromPriceList) {
                                            formAPI.setValue(
                                              getName('addToPriceList'),
                                              true,
                                            );
                                            formAPI.setValue(
                                              getName('updateOnPriceList'),
                                              false,
                                            );
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                  {!revenue._id &&
                                    formMeta.helpers.isOtherRevenue && (
                                      <PriceListButton
                                        onClick={() =>
                                          openPriceListModal(index)
                                        }
                                        disabled={field.disabled}
                                      />
                                    )}
                                </div>
                              )}
                            </FieldConnector>
                          )}

                          {!itemMeta.fields.description.isHidden && (
                            <FieldConnector
                              name={getName('description')}
                              disabled={(name) =>
                                isFieldDisabled(name) ||
                                itemMeta.fields.description.isDisabled
                              }
                              label={`${t('invoice.form.description')} (${t(
                                'field.optional.tag',
                              )})`}
                            >
                              {({ field: { onChange, ...field } }) => (
                                <FreeTextInput
                                  tracing={{
                                    onFocus:
                                      RevenuesEvents.WEBAPP_REVENUE_CLICKEDITEMDESCRIPTION,
                                  }}
                                  {...field}
                                  disabled={field.disabled}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    onChange(value);
                                  }}
                                />
                              )}
                            </FieldConnector>
                          )}

                          <div
                            className={classNames(
                              !itemMeta.fields.quantity.isHidden &&
                                !itemMeta.fields.discountPercentage.isHidden
                                ? 'flex flex-col gap-2'
                                : 'flex gap-2',
                            )}
                          >
                            <FieldConnector
                              className="flex-1"
                              name={getName('categoryId')}
                              disabled={(name) =>
                                isFieldDisabled(name) ||
                                itemMeta.fields.categoryId.isDisabled
                              }
                              label={t('expense.category')}
                            >
                              {({ field: { value, onChange, ...rest } }) => (
                                <CategoriesPickerControlled
                                  {...rest}
                                  pickerController={pickerController}
                                  isDisabled={rest.disabled}
                                  value={value}
                                  taxPeriod={formValues.taxPeriod}
                                  onChange={(category, taxPeriod) =>
                                    itemsCategoryPickerOnChange(
                                      index,
                                      category,
                                      taxPeriod,
                                    )
                                  }
                                />
                              )}
                            </FieldConnector>
                            {(!itemMeta.fields.quantity.isHidden ||
                              !itemMeta.fields.discountPercentage.isHidden) && (
                              <div className="flex gap-2">
                                {!itemMeta.fields.quantity.isHidden && (
                                  <FieldConnector
                                    className="flex-1"
                                    name={getName('quantity')}
                                    disabled={(name) =>
                                      isFieldDisabled(name) ||
                                      itemMeta.fields.quantity.isDisabled
                                    }
                                    label={t('invoice.field.quantity')}
                                  >
                                    {({
                                      field: { onChange, value, ...field },
                                    }) => {
                                      return (
                                        <NumberField
                                          tracing={{
                                            onFocus:
                                              RevenuesEvents.WEBAPP_REVENUE_CLICKEDQUANTITY,
                                          }}
                                          {...field}
                                          disabled={field.disabled}
                                          value={
                                            value == null
                                              ? null
                                              : divideBy1000(value)
                                          }
                                          onChange={(value) => {
                                            onChange(
                                              value == null
                                                ? null
                                                : round(multiplyBy1000(value)),
                                            );
                                          }}
                                        />
                                      );
                                    }}
                                  </FieldConnector>
                                )}

                                {!itemMeta.fields.discountPercentage
                                  .isHidden && (
                                  <FieldConnector
                                    className="flex-1"
                                    name={getName('discountPercentage')}
                                    label={t('invoice.field.discount')}
                                    disabled={(name) =>
                                      isFieldDisabled(name) ||
                                      itemMeta.fields.discountPercentage
                                        .isDisabled
                                    }
                                  >
                                    {({
                                      field: { value, onChange, ...rest },
                                    }) => (
                                      <NumberField
                                        {...rest}
                                        format={{ prefix: '%' }}
                                        value={dividBy10(value || 0)}
                                        onChange={(value) => {
                                          const discValue =
                                            value == null ? 0 : round(value);
                                          onChange(
                                            round(multiplyBy10(discValue)),
                                          );
                                        }}
                                      />
                                    )}
                                  </FieldConnector>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            {(!itemMeta.fields.unitAmountExclVAT.isHidden ||
                              !itemMeta.fields.unitAmountInclVAT.isHidden) && (
                              <FieldConnector
                                className="flex-1 min-w-0"
                                name={
                                  item.doesUnitPriceIncludeVAT
                                    ? getName('unitAmountInclVAT')
                                    : getName('unitAmountExclVAT')
                                }
                                key={
                                  item.doesUnitPriceIncludeVAT
                                    ? 'unitAmountInclVAT'
                                    : 'unitAmountExclVAT'
                                }
                                disabled={isFieldDisabled}
                                label={
                                  formMeta.helpers.isOtherRevenue
                                    ? t('invoice.field.amount')
                                    : t('invoice.field.unit_price')
                                }
                              >
                                {({ field: { onChange, value, ...field } }) => {
                                  const _value = value;
                                  return (
                                    <PaymentAmount
                                      tracing={{
                                        onFocus:
                                          RevenuesEvents.WEBAPP_REVENUE_CLICKEDAMOUNT,
                                      }}
                                      currency={formValues.currency}
                                      {...field}
                                      disabled={
                                        field.disabled ||
                                        (item.doesUnitPriceIncludeVAT
                                          ? itemMeta.fields.unitAmountInclVAT
                                              .isDisabled
                                          : itemMeta.fields.unitAmountExclVAT
                                              .isDisabled)
                                      }
                                      value={
                                        _value == null
                                          ? null
                                          : divideBy1000(_value)
                                      }
                                      onChange={(value) => {
                                        value = value == null ? 0 : value;
                                        const amount = round(value);
                                        onChange(round(multiplyBy1000(amount)));
                                      }}
                                    />
                                  );
                                }}
                              </FieldConnector>
                            )}

                            {!itemMeta.fields.VATRate.isHidden && (
                              <FieldConnector
                                className="flex-1 min-w-0"
                                name={getName('VATRate')}
                                label={t('invoice.field.vat_rate')}
                                disabled={(name) =>
                                  isFieldDisabled(name) ||
                                  itemMeta.fields.VATRate.isDisabled
                                }
                              >
                                {({ field: { onChange, value, ...rest } }) => (
                                  <Select
                                    tracing={{
                                      onMenuOpen:
                                        RevenuesEvents.WEBAPP_REVENUE_CLICKEDVATRATE,
                                      onChange:
                                        RevenuesEvents.WEBAPP_REVENUE_UPDATEDVATRATE,
                                    }}
                                    options={VATRates}
                                    isDisabled={rest.disabled}
                                    isSearchable={false}
                                    value={
                                      value == null ? null : divideBy1000(value)
                                    }
                                    onChange={(option) =>
                                      onRateChange(option.value)
                                    }
                                    formatOptionLabel={(option, meta) => {
                                      if (meta.context === 'value')
                                        return option.label;
                                      return (
                                        <div className="flex items-center justify-between w-full">
                                          <span>{option.label}</span>
                                          {itemMeta.helpers
                                            .recommendedVATRate ===
                                            option.value && (
                                            <Badge
                                              color="purple"
                                              baseClassName="py-1 px-1.5 rounded-full"
                                            >
                                              {t('recommended')}
                                            </Badge>
                                          )}
                                        </div>
                                      );
                                    }}
                                    {...rest}
                                  />
                                )}
                              </FieldConnector>
                            )}

                            {!itemMeta.fields.doesUnitPriceIncludeVAT
                              .isHidden && (
                              <FieldConnector
                                name={getName('doesUnitPriceIncludeVAT')}
                                disabled={(name) =>
                                  isFieldDisabled(name) ||
                                  itemMeta.fields.doesUnitPriceIncludeVAT
                                    .isDisabled
                                }
                                className="flex flex-col items-center gap-2"
                                label={t('invoice.field.vat_toggle.amount')}
                              >
                                {({ field: { value, disabled } }) => (
                                  <Toggle
                                    enabled={!!value}
                                    onChange={onToggleIncludeVAT}
                                    disabled={disabled}
                                  />
                                )}
                              </FieldConnector>
                            )}
                          </div>

                          {itemMeta.helpers.shouldShowRealEstateVATRateInfo && (
                            <Alert
                              className="mb-0"
                              type="info"
                              contentClassName="whitespace-pre-line"
                              description={t(
                                'revenue.vatrate_real_estate_info.text',
                              )}
                            />
                          )}

                          {itemMeta.helpers
                            .shouldShowVATRateBelgianDonationInfo && (
                            <Alert
                              className="mb-0"
                              type="info"
                              contentClassName="whitespace-pre-line"
                              description={t(
                                'revenue.vatrate_donations_info.text',
                              )}
                            />
                          )}

                          {itemMeta.helpers
                            .shouldShowVATRateIntraBusinessWarning && (
                            <Alert
                              className="mb-0"
                              type="warning"
                              title={t(
                                'revenue.vatrate_intra_eu_business_warning.title',
                              )}
                              description={
                                <ReactMarkdown components={markdownComponents}>
                                  {t(
                                    'revenue.vatrate_intra_eu_business_warning.desc',
                                    {
                                      userNationality: t(
                                        'nationality.' + customerCountry,
                                      ),
                                      userCountry: t(
                                        'country.' + customerCountry,
                                      ),
                                    },
                                  )}
                                </ReactMarkdown>
                              }
                            />
                          )}
                          {itemMeta.helpers
                            .shouldShowVATRateExtraPrivateWarning && (
                            <Alert
                              className="mb-0"
                              type="warning"
                              title={t(
                                'revenue.vatrate_extra_eu_private_service_warning.title',
                              )}
                              description={t(
                                'revenue.vatrate_extra_eu_private_service_warning.text',
                              )}
                            />
                          )}
                          {!itemMeta.fields.whyZeroVAT.isHidden && (
                            <FieldConnector
                              name={getName('whyZeroVAT')}
                              disabled={(name) =>
                                isFieldDisabled(name) ||
                                itemMeta.fields.whyZeroVAT.isDisabled
                              }
                              label={t('invoice.field.why_zero_vat.label')}
                            >
                              {({ field }) => (
                                <WhyZeroVAT
                                  tracing={{
                                    onMenuOpen:
                                      RevenuesEvents.WEBAPP_REVENUE_CLICKEDWHYZEROVAT,
                                    onChange:
                                      RevenuesEvents.WEBAPP_REVENUE_UPDATEDWHYZEROVAT,
                                  }}
                                  {...field}
                                  clientLocation={formValues.client?.location}
                                  clientType={formValues.client?.type}
                                  categoryId={item.categoryId}
                                  revenueType={formValues.type}
                                  VATType={formValues.user.VATType}
                                />
                              )}
                            </FieldConnector>
                          )}

                          {!itemMeta.fields.addToPriceList.isHidden && (
                            <FieldConnector name={getName('addToPriceList')}>
                              {({ field }) => (
                                <div className="flex items-center mt-2">
                                  <Checkbox
                                    id={`addToPriceList-${index}`}
                                    checked={!!field.value}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      field.onChange(checked);
                                    }}
                                    className="mr-2"
                                  />
                                  <label
                                    htmlFor={`addToPriceList-${index}`}
                                    className="text-sm"
                                  >
                                    {t('other_revenue.add_to_price_list')}
                                  </label>
                                </div>
                              )}
                            </FieldConnector>
                          )}

                          {!itemMeta.fields.updateOnPriceList.isHidden && (
                            <FieldConnector name={getName('updateOnPriceList')}>
                              {({ field }) => (
                                <div className="flex items-center mt-2">
                                  <Checkbox
                                    id={`updateOnPriceList-${index}`}
                                    checked={!!field.value}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      field.onChange(checked);
                                    }}
                                    className="mr-2"
                                  />
                                  <label
                                    htmlFor={`updateOnPriceList-${index}`}
                                    className="text-sm"
                                  >
                                    {t('other_revenue.update_price_list')}
                                  </label>
                                </div>
                              )}
                            </FieldConnector>
                          )}
                        </div>
                      );
                    }}
                  />
                )}
              </FieldConnector>
            )}
            {!formMeta.helpers.shouldHideAddItemBtn && !isLocked && (
              <div className="flex gap-2">
                <Button
                  structure="text"
                  color="revenues"
                  onClick={() =>
                    itemsController.append({
                      ...itemDefaults,
                      doesUnitPriceIncludeVAT:
                        !!shouldUnitPriceIncludeVATByDefault,
                    })
                  }
                >
                  <PlusIcon className="w-5 mr-1" />
                  {t('invoice.field.item.add_item')}
                </Button>
              </div>
            )}
            {formMeta.helpers.isGenerated && !!revenue._id && (
              <RevenueProjectInvoices
                revenue={revenue}
                onRevenueClick={onChangeRevenue}
              />
            )}
            {priceListModalContent}
            {!formMeta.helpers.isCalcUIHidden && (
              <div>
                {(formMeta.helpers.isInvoice ||
                  formMeta.helpers.isCreditNote) &&
                  formMeta.helpers.isCustomerSubjectToVAT && (
                    <div className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2 my-1">
                      <p className="text-primary-400">
                        {t('invoice.field.total_amount_excl_vat')}
                      </p>
                      <span className="font-semibold">
                        <Amount
                          amount={totalAmountExclVAT}
                          currency={formValues.currency}
                        />
                      </span>
                    </div>
                  )}

                {Object.entries(
                  removeInvalidVATRateGroups(groupBy(allItems, 'VATRate')),
                ).map(([VATGroupName, VATGroup]) => {
                  return (
                    <div
                      key={VATGroupName}
                      className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2 my-1"
                    >
                      <p className="text-primary-400">
                        {t(
                          `invoice.field.total_amount_vat_${(
                            divideBy1000(+VATGroupName) * 100
                          ).toFixed()}`,
                        )}
                      </p>
                      <span className="font-semibold">
                        <Amount
                          currency={formValues.currency}
                          amount={round(
                            VATGroup.reduce((acc, item) => {
                              if (item.doesUnitPriceIncludeVAT) {
                                const sum =
                                  calculateVATAmount(
                                    divideBy1000(item.unitAmountInclVAT),
                                    divideBy1000(+VATGroupName),
                                    { roundResult: false },
                                  ) * divideBy1000(item.quantity);
                                return (
                                  acc +
                                  (sum -
                                    sum *
                                      ((item.discountPercentage || 0) / 1000))
                                );
                              }

                              const sum =
                                divideBy1000(item.unitAmountExclVAT) *
                                divideBy1000(+VATGroupName) *
                                divideBy1000(item.quantity);
                              return (
                                acc +
                                (sum -
                                  sum * ((item.discountPercentage || 0) / 1000))
                              );
                            }, 0),
                          )}
                        />
                      </span>
                    </div>
                  );
                })}
                <div className="bg-[#F9F7FC] flex items-center justify-between rounded-lg p-2 my-1">
                  <p className="text-primary-400">
                    {t('invoice.field.total_amount_incl_vat')}
                  </p>
                  <span className="font-semibold">
                    <Amount
                      currency={formValues.currency}
                      amount={
                        formMeta.helpers.isCreditNote
                          ? totalAmountInclVAT * -1
                          : totalAmountInclVAT
                      }
                    />
                  </span>
                </div>
              </div>
            )}

            {formMeta.helpers.shouldShowAmountExceeds250Warning && (
              <Alert
                type="warning"
                className="mb-0 whitespace-pre-wrap"
                name="ticket with invoice items exceeding 250 euros"
                description={t('invoice.warning_bubble.ticket_exceeds_250')}
              />
            )}

            {!germanCarComputedItemsAPI.isLoading && !!allItems.length && (
              <div className="flex flex-col items-end mt-4">
                <TaxImpact
                  currencyRate={currencyRate}
                  documentType={TaxImpactDocumentType.REVENUE}
                />
              </div>
            )}
            {!formMeta.helpers.isGenerated && (
              <div
                className={classNames('pt-7 flex flex-col gap-4', {
                  'border-t border-primary-100 mt-7': allItems.length,
                })}
              >
                <p className="font-bold text-main-900 text-xl leading-none tracking-tight font-avenir">
                  {t('expense.additional_information')}
                </p>
                {!formMeta.helpers.isGenerated && renderTransactions()}

                {!formMeta.fields.orderReference.isHidden &&
                  !formMeta.helpers.isGenerated &&
                  renderOrderReference()}

                {!formMeta.fields.comments.isHidden && (
                  <FieldConnector
                    name="comments"
                    disabled={(name) =>
                      formMeta.fields.comments.isDisabled ||
                      isFieldDisabled(name)
                    }
                    label={t('invoice.field.notes_and_comments')}
                  >
                    {({ field: { onChange, ...field } }) => (
                      <FreeTextInput
                        type="textarea"
                        {...field}
                        onChange={({ target: { value } }) => {
                          onChange(value);
                        }}
                      />
                    )}
                  </FieldConnector>
                )}

                {!formMeta.fields.notes.isHidden && (
                  <FieldConnector name="notes" label={t('invoices.user_notes')}>
                    {({ field: { onChange, ...field } }) => (
                      <FreeTextInput
                        type="textarea"
                        {...field}
                        onChange={({ target: { value } }) => {
                          onChange(value);
                        }}
                      />
                    )}
                  </FieldConnector>
                )}

                {!formMeta.helpers.isGenerated && renderPeriod()}

                {!formMeta.fields.VATType.isHidden && (
                  <UseState
                    init={false}
                    render={(isOpen, setIsOpen) => {
                      return (
                        <>
                          <Button
                            structure="text"
                            className="self-start"
                            onClick={() => setIsOpen(!isOpen)}
                          >
                            {isOpen ? (
                              <MinusIcon className="w-5 mr-1" />
                            ) : (
                              <PlusIcon className="w-5 mr-1" />
                            )}
                            {t('invoice.item_form.advanced_settings')}
                          </Button>
                          <FieldConnector
                            name={'user.VATType'}
                            disabled={(name) =>
                              formMeta.fields.VATType.isDisabled ||
                              isFieldDisabled(name)
                            }
                            showUI={isOpen}
                            label={t('customer.field.vattype.doctype', {
                              docType: t('invoice'),
                            })}
                          >
                            {({ field: { onChange, ...rest } }) => (
                              <Select
                                tracing={{
                                  onMenuOpen:
                                    RevenuesEvents.WEBAPP_REVENUE_CLICKEDVATTYPE,
                                  onChange:
                                    RevenuesEvents.WEBAPP_REVENUE_UPDATEDVATTYPE,
                                }}
                                menuPlacement="top"
                                isDisabled={rest.disabled}
                                options={[
                                  {
                                    label: t(
                                      'settings.generalTaxes.subjectToVAT',
                                    ),
                                    value: VATTypeEnum.subjectToVAT,
                                  },
                                  {
                                    label: t(
                                      `settings.generalTaxes.franchisee.${customerCountry}`,
                                    ),
                                    value: VATTypeEnum.franchisee,
                                  },
                                  {
                                    label: t('settings.generalTaxes.exempt'),
                                    value: VATTypeEnum.exempt,
                                  },
                                ]}
                                isSearchable={false}
                                onChange={(option: any) => {
                                  onChange(option.value);
                                }}
                                {...rest}
                              />
                            )}
                          </FieldConnector>
                        </>
                      );
                    }}
                  />
                )}

                {
                  <UseState
                    init={false}
                    render={(isOpen, setIsOpen) => {
                      const peppolAttachment = revenue.filePathPeppolDocument;
                      const importedUBL = formValues.filePathUbl31;
                      if (!peppolAttachment && !importedUBL) return null;
                      const downloadNamePrefix = [
                        formValues.client?.name,
                        formValues.revenueNumber,
                      ];
                      return (
                        <>
                          <Button
                            structure="text"
                            className="self-start"
                            onClick={() => setIsOpen(!isOpen)}
                          >
                            {isOpen ? (
                              <MinusIcon className="w-5 mr-1" />
                            ) : (
                              <PlusIcon className="w-5 mr-1" />
                            )}
                            {t('form.attachments')}
                          </Button>
                          {isOpen && (
                            <FilesDownloadList
                              addFileTypeToFileLabels
                              files={[
                                {
                                  label: t('form.attachments.invoice.main'),
                                  path: formValues.filePath,
                                  downloadNamePrefix,
                                },
                                {
                                  label: t('form.attachments.peppol_doc'),
                                  path: peppolAttachment,
                                  downloadNamePrefix,
                                },
                                {
                                  label: t('form.attachments.ubl'),
                                  path: importedUBL,
                                  downloadNamePrefix,
                                },
                              ]}
                            />
                          )}
                        </>
                      );
                    }}
                  />
                }
              </div>
            )}

            {isExpert && (
              <div className="border-t border-primary-100 mt-3 pt-7 flex flex-col gap-4">
                <p className="font-bold text-main-900 text-xl leading-none tracking-tight font-avenir">
                  {t('expense.accountant_section')}
                </p>
                {!formMeta.fields.accountantComments.isHidden && (
                  <FieldConnector
                    name={'accountantReview.comments'}
                    disabled={isFieldDisabled}
                    label={t('accountantReview.comments')}
                  >
                    {({ field }) => (
                      <FreeTextInput type="textarea" {...field} />
                    )}
                  </FieldConnector>
                )}
                <RevenueVATCells invoice={formValues} enabled={!hasErrors} />
              </div>
            )}
            {footerMeta}
          </div>
        </div>
        {!disabled &&
          (!isLocked || (isLocked && isFormDirty)) &&
          (() => {
            const runValidationAndSubmit = async (options?: {
              prepareToSend?: boolean;
            }) => {
              formAPI.setValue('_prepareToSend', !!options?.prepareToSend);
              if (lockByTaxesStatusAPI.data) {
                const lockUI = document.querySelector('.TAX_LOCK_REASON');
                if (lockUI) {
                  lockUI.scrollIntoView({ behavior: 'smooth' });
                  return;
                }
              }
              // temporary
              if (formMeta.helpers.isDeductible && !formValues.taxPeriod) {
                const firstDeductibleItemIndex = formValues.items.findIndex(
                  (item: any, index: number) =>
                    getItemMeta(formValues, item, `items.${index}.`).helpers
                      .isDeductible,
                );
                return pickerController.api.open({
                  value: formValues.items[firstDeductibleItemIndex].categoryId,
                  onChange: (category, taxPeriod) => {
                    itemsCategoryPickerOnChange(
                      firstDeductibleItemIndex,
                      category,
                      taxPeriod,
                    );
                  },
                });
              }

              return formAPI.handleSubmit(
                () => onSubmit(options),
                () => {
                  requestAnimationFrame(() => {
                    const fieldWithError =
                      document.querySelector('.field-with-error');
                    if (fieldWithError) {
                      fieldWithError.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      // we have erros for hidden fields
                      // it means some important data is missing
                      Sentry.captureException(
                        {
                          message: createSentryCustomErrorMsg(
                            'revenues_errors_on_hidden_fields',
                          ),
                        },
                        {
                          extra: {
                            formErrors: JSON.stringify(
                              formAPI.formState.errors,
                            ),
                            formValues: JSON.stringify(formValues),
                          },
                        },
                      );
                    }
                  });
                },
              )();
            };
            const isNotChangedGeneratedInvoice =
              formMeta.helpers.isGenerated &&
              !isFormDirty &&
              revenue.status !== InvoiceStatusEnum.draft;
            return (
              <StickyControlsContainer className="px-6">
                {isWeb && (
                  <>
                    {formMeta.helpers.isNotSent &&
                    !formMeta.helpers.isSelfBilling &&
                    !isLocked ? (
                      <Button
                        structure="secondary"
                        className="flex-1 leading-[1.5rem]"
                        loading={
                          isSubmitting && formAPI.getValues('_prepareToSend')
                        }
                        disabled={isSubmitting}
                        onClick={() =>
                          runValidationAndSubmit({ prepareToSend: true })
                        }
                        renderIcon={() => (
                          <EmailSendOutLineIcon className="w-6 h-6 mr-2" />
                        )}
                      >
                        {isNotChangedGeneratedInvoice
                          ? t('send')
                          : t('save_and_send')}
                      </Button>
                    ) : (
                      <Button
                        structure="secondary"
                        className="flex-1 leading-[1.5rem] !border-primary-100 text-primary-600"
                        color="primary"
                        disabled={isSubmitting}
                        onClick={onClose}
                      >
                        {t('close')}
                      </Button>
                    )}
                    <Button
                      highlightID="submit-revenue"
                      className="flex-1 leading-[1.5rem]"
                      loading={
                        isSubmitting && !formAPI.getValues('_prepareToSend')
                      }
                      disabled={isSubmitting || isNotChangedGeneratedInvoice}
                      tracingEvent={RevenuesEvents.WEBAPP_REVENUE_CLICKEDSAVE}
                      onClick={() => runValidationAndSubmit()}
                    >
                      {t('save')}
                    </Button>
                  </>
                )}
                {isExpert && (
                  <>
                    <Tooltip
                      content={
                        customerCountry === AvailableCountries.germany
                          ? formValues.status !== InvoiceStatusEnum.paid
                            ? t('invoice.cannot_validate_not_paid')
                            : null
                          : formValues.status === InvoiceStatusEnum.notSent
                          ? t('invoice.cannot_validate_not_sent')
                          : null
                      }
                      placement="top"
                    >
                      <div className="inline-block w-full">
                        <Button
                          loading={isSubmitting && isReviewed}
                          disabled={
                            isSubmitting ||
                            (customerCountry === AvailableCountries.germany
                              ? formValues.status !== InvoiceStatusEnum.paid
                              : formValues.status === InvoiceStatusEnum.notSent)
                          }
                          className="w-full"
                          tracingEvent={
                            RevenuesEvents.WEBAPP_REVENUE_CLICKEDSAVE
                          }
                          onClick={() => {
                            formAPI.setValue(
                              'accountantReview.reviewStatus',
                              ReviewStatusEnum.reviewed,
                            );
                            runValidationAndSubmit();
                          }}
                        >
                          {t('save_and_validate')}
                        </Button>
                      </div>
                    </Tooltip>
                    <Button
                      structure="secondary"
                      loading={isSubmitting && !isReviewed}
                      disabled={isSubmitting}
                      className="w-full"
                      tracingEvent={RevenuesEvents.WEBAPP_REVENUE_CLICKEDSAVE}
                      onClick={() => {
                        formAPI.setValue(
                          'accountantReview.reviewStatus',
                          ReviewStatusEnum.notReviewed,
                        );
                        runValidationAndSubmit();
                      }}
                    >
                      {t('save_and_dont_validate')}
                    </Button>
                  </>
                )}
              </StickyControlsContainer>
            );
          })()}
      </form>
    </FormProvider>
  );
};

export default Form;
